import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Row} from 'react-bootstrap';
import {getVfdClassesList, getVfdManufacturersList} from 'api/DevicesApi';
import {vfdParityOptions} from 'constants/apiConstants';
import {useAppContext} from 'contexts/AppContext';
import HiddenInput from 'components/Form/HiddenInput';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Switch from 'components/Form/Switch';

const DEFAULT_MOTION_OPTION = {label: '-- Select motion --', value: ''};
const DEFAULT_VFD_MANUFACTURER_OPTION = {label: '-- Select VFD manufacturer --', value: ''};
const DEFAULT_VFD_CLASS_OPTION = {label: '-- Select VFD class --', value: ''};

export default function VfdForm({
    values, initialValues, dirty, isValid, isSubmitting, handleSubmit, handleChange, setFieldValue,
    motions, isEditable, isLoading, onCloseButtonClick
}) {
    const isMounted = useRef(false);
    const isDisabled = !isEditable || isLoading;
    const [vfdManufacturerOptions, setVfdManufacturerOptions] = useState([DEFAULT_VFD_MANUFACTURER_OPTION]);
    const [vfdClassOptions, setVfdClassOptions] = useState([DEFAULT_VFD_CLASS_OPTION]);
    const {handleApiError} = useAppContext();

    useEffect(() => {
        isMounted.current = true;
        loadVfdManufacturerOptions();
        return () => isMounted.current = false;
    }, []);

    useEffect(() => {
        if (initialValues.manufacturer_reference) loadVfdClassOptions(initialValues.manufacturer_reference);
    }, [initialValues]);

    const motionOptions = [
        DEFAULT_MOTION_OPTION,
        ...Object.values(motions).map(motion => ({label: motion.motion_name, value: motion.id}))
    ];

    const loadVfdManufacturerOptions = () => {
        const onSuccess = response => {
            const options = [DEFAULT_VFD_MANUFACTURER_OPTION];
            response.data
                .filter(manufacturer => manufacturer.is_supported)
                .forEach(manufacturer => options.push({label: manufacturer.name, value: manufacturer.id}));
            if (isMounted.current) setVfdManufacturerOptions(options);
        };
        getVfdManufacturersList(onSuccess, handleApiError, () => {});
    };

    const loadVfdClassOptions = (manufacturerId, manufacturerChanged = false) => {
        if (manufacturerChanged) setFieldValue('class_reference', '', false);
        if (!manufacturerId) {
            setVfdClassOptions([DEFAULT_VFD_CLASS_OPTION]);
            return;
        }
        const onSuccess = response => {
            const options = [DEFAULT_VFD_CLASS_OPTION];
            response.data
                .filter(vfdClass => vfdClass.norlink_support)
                .forEach(vfdClass => options.push({label: vfdClass.name, value: vfdClass.id}));
            if (isMounted.current) setVfdClassOptions(options);
        };
        getVfdClassesList(manufacturerId, onSuccess, handleApiError, () => {});
    };

    const addressLabel = values.crane_communication_type === 'PLC' ? 'Address (e.g. 192.168.1.100)' : 'Address (e.g. 0x1F)';
    const showModbusRTUSettings = values.crane_communication_type === 'MODBUSRTU';

    const handleManufacturerChange = (e) => {
        handleChange(e);
        loadVfdClassOptions(e.target.value, true);
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Input label="Name" type="text" name="vfd_name" required disabled={isDisabled} />
                <Select label="Motion" name="motion_reference" options={motionOptions} required disabled={isDisabled} />
            </Row>
            <Row>
                <Select label="Manufacturer" name="manufacturer_reference" options={vfdManufacturerOptions} onChange={handleManufacturerChange} required disabled={isDisabled} />
                <Select label="Class" name="class_reference" options={vfdClassOptions} required disabled={isDisabled} />
            </Row>
            <Row>
                <Input label="Part number" type="text" name="vfd_part_no" required disabled={isDisabled} />
                <Input label="Serial number" type="text" name="vfd_serial" required disabled={isDisabled} />
                <Input label="Current rated" type="number" min="0" step="0.01" name="current_rated" required disabled={isDisabled} />
            </Row>
            <Row>
                <Input label="Crane communication type" type="text" name="crane_communication_type" disabled />
                <Input label={addressLabel} type="text" name="mb_address" required disabled={isDisabled} />
            </Row>
            {showModbusRTUSettings &&
                <Row>
                    <Input label="Baud rate (e.g. 9600)" type="number" min="300" step="100" name="mb_baud_rate" required disabled={isDisabled} />
                    <Select label="Parity" name="mb_parity" options={vfdParityOptions} required disabled={isDisabled} />
                </Row>}
            <Row>
                <Switch label="Active" id="active" name="active" value="1" disabled={isDisabled} />
            </Row>
            <HiddenInput name="id" />
            <HiddenInput name="ts" />
            {isEditable && <Button type="submit" className="btn-form-submit" disabled={!dirty || !isValid || isSubmitting || isDisabled}>Save</Button>}
            <Button type="button" className="btn-form-close" onClick={onCloseButtonClick} disabled={isSubmitting}>Close</Button>
        </Form>
    )
};
